import { useState, useEffect, useCallback } from "react";
import {
  Droplets, Footprints, Flame, Beef, Candy, Apple,
  Award, Target, Activity, Sparkles, ChevronDown,
  Scale,
} from "lucide-react";
import { supabase } from "../supabase";
import { getDeviceId } from "../utils/device";
import type { Language } from "../data";
import type { UserProfile } from "../types";
import {
  createEmptyAgg, addMealNutrition, addScanNutrition,
  isKnownZero, isUnknown, isFullyKnown, isPartial, localDateString, localDayRange,
  type MealNutritionAgg,
} from "../utils/nutritionAgg";
import {
  resolveCalorieTarget, resolveProteinTarget, resolveSugarLimit,
  resolveWaterTarget, resolveStepsTarget, targetSourceLabel,
} from "../utils/targets";

interface DailyReportProps {
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  lang: Language;
  userProfile: UserProfile | null;
  userId?: string | null;
}

interface DayData {
  water_glasses: number;
  water_target: number;
  water_sips: number;
  steps: number;
  steps_goal: number;
  steps_calories: number;
  steps_distance_km: number;
  food: MealNutritionAgg;
  exercise_cal: number;
}

interface WLGoal {
  start_weight_kg: number;
  target_weight_kg: number;
  target_date: string;
  weekly_target_kg: number;
}

const T = {
  ar: {
    title: "التقييم اليومي",
    subtitle: "ملخص إنجازاتك الصحية في نهاية اليوم",
    water: "شرب الماء",
    steps: "المشي",
    protein: "البروتين",
    sugar: "السكريات",
    calories: "السعرات",
    goal: "الهدف",
    achieved: "تم تحقيقه",
    progress: "التقدم",
    excellent: "ممتاز! حققت أهدافك",
    good: "أداء جيد، استمر",
    fair: "أداء مقبول، يحتاج تحسين",
    poor: "تحتاج لبذل جهد أكبر غداً",
    insufficientData: "بيانات غير كافية",
    insufficientDataDesc: "سجّل وجباتك لرؤية التقييم الغذائي الكامل",
    activityProgress: "تقدم النشاط",
    nutritionProgress: "تقدم التغذية",
    adviceTitle: "نصائح لغدٍ أفضل",
    stepsAdvice: {
      low: "حاول المشي 5000 خطوة على الأقل غداً — ابدأ بخطوات صغيرة",
      mid: "أنت على الطريق الصحيح! استهدف 10000 خطوة غداً",
      high: "أداء رائع! حافظ على هذا المستوى من النشاط",
    },
    waterAdvice: {
      low: "اشرب 8 أكواب ماء على الأقل غداً — الترطيب مهم لصحتك",
      mid: "تحسنت في شرب الماء، استهدف الوصول للهدف كاملاً غداً",
      high: "ممتاز في الترطيب! استمر على هذا النمط",
    },
    proteinAdvice: {
      low: "زد البروتين في وجباتك غداً — الدجاج والسمك والبقوليات خيارات رائعة",
      mid: "مستوى بروتين مقبول، حاول الوصول للهدف الكامل غداً",
      high: "ممتاز في تناول البروتين! عضلاتك تشكرك",
    },
    sugarAdvice: {
      low: "ممتاز! حافظت على سكر منخفض — استمر كذلك",
      mid: "حاول تقليل السكريات غداً — استبدلها بالفواكه الطبيعية",
      high: "قلل السكريات غداً — الإفراط فيها يضر بصحتك",
    },
    calAdvice: {
      low: "ممتاز! التزمت بحدود السعرات المحددة لهدفك",
      mid: "قريب من هدف السعرات — حافظ على الانضباط غداً",
      high: "تجاوزت هدف السعرات اليوم — راجع وجباتك غداً",
    },
    summary: "الملخص",
    todayReport: "تقرير اليوم",
    noData: "لا توجد بيانات كافية لعرض التقرير",
    noFoodData: "لم يتم تسجيل أي وجبات اليوم — سجّل وجباتك لرؤية التقييم الغذائي",
    partialData: "بيانات غذائية غير مكتملة — بعض القيم غير معروفة",
    sugarUnknown: "السكر غير معروف — لا يمكن تقييم مستوى السكر",
    sugarKnownZero: "السكر: 0 جم — ممتاز، بدون سكر مضاف",
    notAvailable: "غير متوفر",
    perDay: "يومياً",
    glasses: "كوب",
    stepsUnit: "خطوة",
    km: "كم",
    meals: "وجبة",
    macros: "العناصر الغذائية",
    proteinG: "بروتين (جم)",
    carbsG: "كربوهيدرات (جم)",
    fatG: "دهون (جم)",
    sugarG: "سكر (جم)",
    fiberG: "ألياف (جم)",
    burned: "محروق",
    consumed: "مستهلك",
    net: "الصافي",
    remaining: "متبقٍ",
    over: "تجاوز",
    tomorrowGoals: "أهداف الغد المقترحة",
    stayHydrated: "حافظ على ترطيب كافٍ",
    walkMore: "زد نشاطك البدني",
    balancedMeals: "تناول وجبات متوازنة",
    reduceSugar: "قلل السكريات المعالجة",
    goodNight: "نم جيداً — النوم أساس الصحة",
    withinCalorieGoal: "ضمن هدف السعرات",
    weightLossProgress: "تقدم إنقاص الوزن",
    lostSoFar: "فقدت حتى الآن",
    targetWeight: "الوزن المستهدف",
    daysLeft: "يوم متبق",
    noWeightGoal: "لم يتم ضبط هدف لإنقاص الوزن بعد",
    dailyCalorieTarget: "هدف السعرات اليومي",
    basedOnWeightGoal: "مبني على هدف إنقاص الوزن",
    generalTarget: "هدف عام",
    configuredTarget: "هدف مخصص",
    fallbackTarget: "هدف افتراضي",
  },
  en: {
    title: "Daily Health Report",
    subtitle: "Summary of your health achievements at the end of the day",
    water: "Water Intake",
    steps: "Steps",
    protein: "Protein",
    sugar: "Sugar",
    calories: "Calories",
    goal: "Goal",
    achieved: "Achieved",
    progress: "Progress",
    excellent: "Excellent! You met your goals",
    good: "Good performance, keep going",
    fair: "Fair performance, needs improvement",
    poor: "You need to put more effort tomorrow",
    insufficientData: "Insufficient data",
    insufficientDataDesc: "Log your meals to see full nutrition scores",
    activityProgress: "Activity Progress",
    nutritionProgress: "Nutrition Progress",
    adviceTitle: "Tips for a Better Tomorrow",
    stepsAdvice: {
      low: "Try to walk at least 5,000 steps tomorrow — start small",
      mid: "You're on the right track! Aim for 10,000 steps tomorrow",
      high: "Great performance! Maintain this activity level",
    },
    waterAdvice: {
      low: "Drink at least 8 glasses of water tomorrow — hydration is key",
      mid: "Improving on water intake — aim to reach your full goal tomorrow",
      high: "Excellent hydration! Keep up this habit",
    },
    proteinAdvice: {
      low: "Add more protein to your meals tomorrow — chicken, fish, legumes are great",
      mid: "Decent protein level — try to reach your full target tomorrow",
      high: "Great protein intake! Your muscles thank you",
    },
    sugarAdvice: {
      low: "Excellent! Kept sugar low — keep it up",
      mid: "Try to reduce sugar tomorrow — replace with natural fruits",
      high: "Cut down on sugar tomorrow — excess harms your health",
    },
    calAdvice: {
      low: "Excellent! You stayed within your calorie target",
      mid: "Close to your calorie goal — maintain discipline tomorrow",
      high: "You exceeded your calorie goal today — review your meals tomorrow",
    },
    summary: "Summary",
    todayReport: "Today's Report",
    noData: "Not enough data to show the report",
    noFoodData: "No meals logged today — log your meals to see nutrition scores",
    partialData: "Incomplete nutrition data — some values are unknown",
    sugarUnknown: "Sugar unknown — cannot assess sugar level",
    sugarKnownZero: "Sugar: 0g — excellent, no added sugar",
    notAvailable: "N/A",
    perDay: "daily",
    glasses: "glasses",
    stepsUnit: "steps",
    km: "km",
    meals: "meals",
    macros: "Macronutrients",
    proteinG: "Protein (g)",
    carbsG: "Carbs (g)",
    fatG: "Fat (g)",
    sugarG: "Sugar (g)",
    fiberG: "Fiber (g)",
    burned: "Burned",
    consumed: "Consumed",
    net: "Net",
    remaining: "remaining",
    over: "over",
    tomorrowGoals: "Suggested Goals for Tomorrow",
    stayHydrated: "Stay well hydrated",
    walkMore: "Increase your physical activity",
    balancedMeals: "Eat balanced meals",
    reduceSugar: "Reduce processed sugar",
    goodNight: "Sleep well — sleep is the foundation of health",
    withinCalorieGoal: "Within calorie goal",
    weightLossProgress: "Weight Loss Progress",
    lostSoFar: "Lost so far",
    targetWeight: "Target weight",
    daysLeft: "days left",
    noWeightGoal: "No weight loss goal set yet",
    dailyCalorieTarget: "Daily Calorie Target",
    basedOnWeightGoal: "Based on your weight loss goal",
    generalTarget: "General target",
    configuredTarget: "Custom target",
    fallbackTarget: "Default target",
  },
};

function pct(achieved: number, goal: number): number {
  if (goal <= 0) return 0;
  return Math.min(100, Math.round((achieved / goal) * 100));
}

function level(p: number): "excellent" | "good" | "fair" | "poor" {
  if (p >= 90) return "excellent";
  if (p >= 65) return "good";
  if (p >= 40) return "fair";
  return "poor";
}

function ProgressBar({ value, color }: { value: number; color: string }) {
  return (
    <div className="h-2.5 bg-stone-100 dark:bg-slate-700 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${value}%`, background: color }}
      />
    </div>
  );
}

function daysLeft(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function DailyReport({ dir, lang, userProfile, userId }: DailyReportProps) {
  const [data, setData] = useState<DayData | null>(null);
  const [wlGoal, setWlGoal] = useState<WLGoal | null>(null);
  const [latestWeight, setLatestWeight] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [currentDay, setCurrentDay] = useState(localDateString());
  const tr = T[lang];
  const deviceId = getDeviceId();

  const loadData = useCallback(async () => {
    setLoading(true);
    const today = localDateString();

    let waterGlasses = 0, waterTarget = 8, waterSips = 0;
    let steps = 0, stepsGoal = 10000, stepsCalories = 0, stepsDistance = 0;
    let exerciseCal = 0;
    const food = createEmptyAgg();

    try {
      let gq = supabase.from("weight_loss_goals").select("start_weight_kg, target_weight_kg, target_date, weekly_target_kg").eq("device_id", deviceId);
      if (userId) gq = gq.or(`user_id.eq.${userId}`);
      const { data: g } = await gq.maybeSingle();
      if (g) setWlGoal(g as WLGoal);
    } catch {}

    try {
      let wq = supabase.from("weight_entries").select("weight_kg, entry_date").eq("device_id", deviceId).order("entry_date", { ascending: false }).limit(1);
      const { data: w } = await wq.maybeSingle();
      if (w) setLatestWeight(Number(w.weight_kg));
    } catch {}

    try {
      const { data: w } = await supabase
        .from("water_intake")
        .select("glasses, target, sips")
        .eq("device_id", deviceId)
        .eq("date", today)
        .maybeSingle();
      if (w) {
        waterGlasses = w.glasses ?? 0;
        waterTarget = w.target ?? 8;
        waterSips = w.sips ?? 0;
      }
    } catch {}

    try {
      const { data: dl } = await supabase
        .from("daily_logs")
        .select("water_glasses, steps, exercise_cal")
        .eq("device_id", deviceId)
        .eq("log_date", today)
        .maybeSingle();
      if (dl) {
        if (dl.water_glasses && dl.water_glasses > waterGlasses) waterGlasses = dl.water_glasses;
        exerciseCal = dl.exercise_cal ?? 0;
      }
    } catch {}

    try {
      let sq = supabase.from("daily_steps").select("steps, goal, calories, distance_km").eq("date", today);
      if (userId) sq = sq.eq("user_id", userId);
      else sq = sq.eq("device_id", deviceId);
      const { data: s } = await sq.maybeSingle();
      if (s) {
        steps = s.steps ?? 0;
        stepsGoal = s.goal ?? 10000;
        stepsCalories = s.calories ?? 0;
        stepsDistance = s.distance_km ?? 0;
      }
    } catch {}

    try {
      const { data: meals } = await supabase
        .from("meal_entries")
        .select("calories, protein_g, carbs_g, fat_g, fiber_g, sugar_g")
        .eq("device_id", deviceId)
        .eq("log_date", today);
      if (meals && meals.length > 0) {
        for (const m of meals) {
          addMealNutrition(food, m);
        }
      }
    } catch {}

    try {
      const { start, end } = localDayRange(today);
      const { data: scans } = await supabase
        .from("food_scans")
        .select("calories, recipe_data, created_at")
        .eq("device_id", deviceId)
        .gte("created_at", start)
        .lt("created_at", end);
      if (scans) {
        for (const s of scans) {
          addScanNutrition(food, s);
        }
      }
    } catch {}

    setData({
      water_glasses: waterGlasses,
      water_target: waterTarget,
      water_sips: waterSips,
      steps,
      steps_goal: stepsGoal,
      steps_calories: stepsCalories,
      steps_distance_km: stepsDistance,
      food,
      exercise_cal: exerciseCal,
    });
    setLoading(false);
  }, [deviceId, userId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newDay = localDateString();
      if (newDay !== currentDay) {
        setCurrentDay(newDay);
        loadData();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [currentDay, loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-stone-300 dark:border-slate-600 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const calResolved = resolveCalorieTarget(userProfile, wlGoal);
  const calTarget = calResolved.calories;
  const calTargetLabel = targetSourceLabel(calResolved.source, {
    weightGoal: tr.basedOnWeightGoal,
    profile: tr.generalTarget,
    fallback: tr.fallbackTarget,
    configured: tr.configuredTarget,
  });

  const proteinResolved = resolveProteinTarget(userProfile);
  const proteinGoal = proteinResolved.protein_g;
  const sugarLimit = resolveSugarLimit(userProfile);

  const waterResolved = resolveWaterTarget(data.water_target);
  const stepsResolved = resolveStepsTarget(data.steps_goal);

  const totalBurned = data.steps_calories + data.exercise_cal;
  const netCalories = data.food.calories.value - totalBurned;

  const waterPct = pct(data.water_glasses, waterResolved.glasses);
  const stepsPct = pct(data.steps, stepsResolved.steps);

  const hasFoodData = data.food.hasAnyFood;
  const isPartialData = hasFoodData && isPartial(data.food);

  const proteinKnown = hasFoodData && isFullyKnown(data.food.protein);
  const proteinPct = proteinKnown ? pct(data.food.protein.value, proteinGoal) : null;

  const sugarKnown = hasFoodData && isFullyKnown(data.food.sugar);
  const sugarPct = sugarKnown ? pct(data.food.sugar.value, sugarLimit) : null;

  const calKnown = hasFoodData && isFullyKnown(data.food.calories);
  const calPct = calKnown ? pct(data.food.calories.value, calTarget) : null;

  const calorieAdherence = calKnown
    ? (data.food.calories.value <= calTarget
      ? Math.min(100, Math.round((data.food.calories.value / calTarget) * 100))
      : Math.max(0, Math.round((calTarget / data.food.calories.value) * 100)))
    : null;

  const hasFullNutritionData = hasFoodData && proteinKnown && sugarKnown && calKnown;
  const components: number[] = [];
  components.push(waterPct);
  components.push(stepsPct);
  if (proteinKnown) components.push(proteinPct!);
  if (sugarKnown) components.push(100 - sugarPct!);
  if (calKnown) components.push(calorieAdherence!);

  const overallPct = hasFullNutritionData
    ? Math.round(components.reduce((a, b) => a + b, 0) / components.length)
    : null;

  const overallLevel = overallPct !== null ? level(overallPct) : null;

  const startWeight = wlGoal ? Number(wlGoal.start_weight_kg) : (userProfile?.weight_kg ?? 0);
  const targetWeight = wlGoal ? Number(wlGoal.target_weight_kg) : 0;
  const totalToLose = Math.max(0, startWeight - targetWeight);
  const lostSoFar = latestWeight != null ? Math.max(0, startWeight - latestWeight) : 0;
  const wlProgressPct = totalToLose > 0 ? Math.min(100, Math.round((lostSoFar / totalToLose) * 100)) : 0;

  const advice: string[] = [];
  advice.push(stepsPct >= 80 ? tr.stepsAdvice.high : stepsPct >= 40 ? tr.stepsAdvice.mid : tr.stepsAdvice.low);
  advice.push(waterPct >= 80 ? tr.waterAdvice.high : waterPct >= 40 ? tr.waterAdvice.mid : tr.waterAdvice.low);
  if (proteinKnown) {
    advice.push(proteinPct! >= 80 ? tr.proteinAdvice.high : proteinPct! >= 40 ? tr.proteinAdvice.mid : tr.proteinAdvice.low);
  }
  if (sugarKnown) {
    advice.push(sugarPct! <= 50 ? tr.sugarAdvice.low : sugarPct! <= 80 ? tr.sugarAdvice.mid : tr.sugarAdvice.high);
  }
  if (calKnown) {
    advice.push(calPct! <= 90 ? tr.calAdvice.low : calPct! <= 110 ? tr.calAdvice.mid : tr.calAdvice.high);
  }

  const tomorrowGoals = [
    tr.stayHydrated,
    tr.walkMore,
    tr.balancedMeals,
    tr.reduceSugar,
    tr.goodNight,
  ];

  const sugarDisplay = !hasFoodData
    ? tr.notAvailable
    : isUnknown(data.food.sugar)
    ? tr.sugarUnknown
    : isKnownZero(data.food.sugar)
    ? tr.sugarKnownZero
    : `${Math.round(data.food.sugar.value)} ${lang === "ar" ? "جم" : "g"}`;

  const dash = tr.notAvailable;

  const cards = [
    {
      id: "water",
      icon: <Droplets className="w-5 h-5 text-white" />,
      label: tr.water,
      achieved: `${data.water_glasses} ${tr.glasses}`,
      goal: `${waterResolved.glasses} ${tr.glasses}`,
      pct: waterPct,
      pctDisplay: `${waterPct}%`,
      color: "linear-gradient(135deg, #0ea5e9, #38bdf8)",
      bgColor: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
      details: data.water_sips > 0 ? `${data.water_sips} ${lang === "ar" ? "رشفة" : "sips"}` : undefined,
    },
    {
      id: "steps",
      icon: <Footprints className="w-5 h-5 text-white" />,
      label: tr.steps,
      achieved: `${data.steps.toLocaleString()} ${tr.stepsUnit}`,
      goal: `${stepsResolved.steps.toLocaleString()} ${tr.stepsUnit}`,
      pct: stepsPct,
      pctDisplay: `${stepsPct}%`,
      color: "linear-gradient(135deg, #14b8a6, #2dd4bf)",
      bgColor: "linear-gradient(135deg, #f0fdfa, #ccfbf1)",
      details: `${data.steps_distance_km.toFixed(2)} ${tr.km} • ${Math.round(totalBurned)} ${tr.burned}`,
    },
    {
      id: "protein",
      icon: <Beef className="w-5 h-5 text-white" />,
      label: tr.protein,
      achieved: hasFoodData ? `${Math.round(data.food.protein.value)} ${lang === "ar" ? "جم" : "g"}` : dash,
      goal: `${proteinGoal} ${lang === "ar" ? "جم" : "g"}`,
      pct: proteinPct ?? 0,
      pctDisplay: proteinPct !== null ? `${proteinPct}%` : dash,
      color: "linear-gradient(135deg, #f97316, #fb923c)",
      bgColor: "linear-gradient(135deg, #fff7ed, #ffedd5)",
      details: hasFoodData && isUnknown(data.food.protein) ? tr.partialData : undefined,
    },
    {
      id: "sugar",
      icon: <Candy className="w-5 h-5 text-white" />,
      label: tr.sugar,
      achieved: sugarDisplay,
      goal: `${sugarLimit} ${lang === "ar" ? "جم" : "g"}`,
      pct: sugarPct ?? 0,
      pctDisplay: sugarPct !== null ? `${sugarPct}%` : dash,
      color: "linear-gradient(135deg, #ec4899, #f472b6)",
      bgColor: "linear-gradient(135deg, #fdf2f8, #fce7f3)",
      details: hasFoodData && isUnknown(data.food.sugar) ? tr.sugarUnknown : undefined,
    },
  ];

  const levelColors: Record<string, { bg: string; text: string; icon: string }> = {
    excellent: { bg: "from-emerald-500 to-green-500", text: "text-emerald-600 dark:text-emerald-400", icon: "🎉" },
    good: { bg: "from-teal-500 to-cyan-500", text: "text-teal-600 dark:text-teal-400", icon: "👍" },
    fair: { bg: "from-amber-500 to-orange-500", text: "text-amber-600 dark:text-amber-400", icon: "💪" },
    poor: { bg: "from-rose-500 to-red-500", text: "text-rose-600 dark:text-rose-400", icon: "🎯" },
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto pb-32" dir={dir}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center shadow-md">
          <Activity className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-black text-stone-800 dark:text-slate-100 text-lg leading-tight">{tr.title}</h2>
          <p className="text-xs text-stone-400 dark:text-slate-500">{tr.subtitle}</p>
        </div>
      </div>

      {wlGoal && (
        <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-500/10 dark:to-teal-500/10 border border-emerald-100 dark:border-emerald-500/20">
          <div className="flex items-center gap-2 mb-3">
            <Scale className="w-4 h-4 text-emerald-500" />
            <h3 className="text-sm font-bold text-emerald-700 dark:text-emerald-300">{tr.weightLossProgress}</h3>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-2.5 bg-emerald-100 dark:bg-emerald-900/40 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-700" style={{ width: `${wlProgressPct}%` }} />
              </div>
              <div className="flex justify-between mt-2 text-[11px] text-emerald-600 dark:text-emerald-400">
                <span>{tr.lostSoFar}: {lostSoFar.toFixed(1)} {lang === "ar" ? "كجم" : "kg"}</span>
                <span>{tr.targetWeight}: {targetWeight} {lang === "ar" ? "كجم" : "kg"}</span>
              </div>
            </div>
            <div className="text-center shrink-0">
              <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{wlProgressPct}%</p>
              {wlGoal.target_date && (
                <p className="text-[10px] text-emerald-500 dark:text-emerald-500">{daysLeft(wlGoal.target_date)} {tr.daysLeft}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {overallPct !== null ? (
        <div
          className="rounded-3xl p-6 text-center shadow-xl"
          style={{
            background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
            boxShadow: "0 20px 60px -10px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
          }}
        >
          <style>{`.dark .overall-card { background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%) !important; }`}</style>
          <div className={`mx-auto w-20 h-20 rounded-full bg-gradient-to-br ${levelColors[overallLevel!].bg} flex items-center justify-center shadow-lg mb-4`}>
            <span className="text-3xl font-black text-white">{overallPct}%</span>
          </div>
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="text-2xl">{levelColors[overallLevel!].icon}</span>
            <h3 className={`text-lg font-black ${levelColors[overallLevel!].text}`}>
              {overallLevel === "excellent" ? tr.excellent : overallLevel === "good" ? tr.good : overallLevel === "fair" ? tr.fair : tr.poor}
            </h3>
          </div>
          <p className="text-xs text-stone-400 dark:text-slate-500">
            {new Date().toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { weekday: "long", month: "long", day: "numeric" })}
          </p>
        </div>
      ) : (
        <div
          className="rounded-3xl p-6 text-center shadow-xl"
          style={{
            background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
            boxShadow: "0 20px 60px -10px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
          }}
        >
          <style>{`.dark .overall-card { background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%) !important; }`}</style>
          <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-stone-400 to-stone-500 flex items-center justify-center shadow-lg mb-4">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-black text-stone-500 dark:text-slate-400 mb-1">{tr.insufficientData}</h3>
          <p className="text-xs text-stone-400 dark:text-slate-500">{tr.insufficientDataDesc}</p>
          <p className="text-xs text-stone-400 dark:text-slate-500 mt-2">
            {new Date().toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { weekday: "long", month: "long", day: "numeric" })}
          </p>
        </div>
      )}

      {!hasFoodData && (
        <div className="rounded-2xl p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-center">
          <p className="text-sm text-amber-700 dark:text-amber-400 leading-relaxed">{tr.noFoodData}</p>
        </div>
      )}

      {isPartialData && (
        <div className="rounded-2xl p-4 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 text-center">
          <p className="text-sm text-orange-700 dark:text-orange-400 leading-relaxed">{tr.partialData}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c) => (
          <div
            key={c.id}
            className="rounded-2xl border border-stone-100 dark:border-slate-700 p-4 shadow-sm cursor-pointer transition-all hover:shadow-md"
            style={{ background: c.bgColor }}
            onClick={() => setExpanded(expanded === c.id ? null : c.id)}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ background: c.color }}>
                {c.icon}
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-stone-700 dark:text-slate-200">{c.label}</h4>
                <p className="text-[11px] text-stone-500 dark:text-slate-400">
                  {c.achieved} / {c.goal}
                </p>
              </div>
              <span className={`text-lg font-black ${c.pctDisplay === dash ? "text-stone-400 dark:text-slate-500" : "text-stone-700 dark:text-slate-200"}`}>{c.pctDisplay}</span>
            </div>
            <ProgressBar value={c.pct} color={c.color} />
            {c.details && (
              <div className="mt-2 flex items-center gap-1 text-[11px] text-stone-500 dark:text-slate-400">
                <ChevronDown className={`w-3 h-3 transition-transform ${expanded === c.id ? "rotate-180" : ""}`} />
                {c.details}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <h3 className="text-sm font-bold text-stone-700 dark:text-slate-200">{tr.calories}</h3>
          <span className="ms-auto text-[10px] text-stone-400 dark:text-slate-500">{calTargetLabel}</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 rounded-xl bg-orange-50 dark:bg-orange-500/10">
            <p className="text-lg font-black text-orange-600 dark:text-orange-400">{hasFoodData ? Math.round(data.food.calories.value) : dash}</p>
            <p className="text-[10px] text-stone-500 dark:text-slate-400">{tr.consumed}</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-teal-50 dark:bg-teal-500/10">
            <p className="text-lg font-black text-teal-600 dark:text-teal-400">{Math.round(totalBurned)}</p>
            <p className="text-[10px] text-stone-500 dark:text-slate-400">{tr.burned}</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-blue-50 dark:bg-blue-500/10">
            <p className="text-lg font-black text-blue-600 dark:text-blue-400">{hasFoodData ? Math.round(netCalories) : dash}</p>
            <p className="text-[10px] text-stone-500 dark:text-slate-400">{tr.net}</p>
          </div>
        </div>
        {hasFoodData && calKnown && (
          <div className="flex items-center justify-between text-xs text-stone-500 dark:text-slate-400 pt-1">
            <span>{tr.goal}: {calTarget} kcal</span>
            <span className={netCalories > calTarget ? "text-rose-500 font-bold" : "text-emerald-500 font-bold"}>
              {netCalories > calTarget
                ? `${tr.over} +${Math.round(netCalories - calTarget)}`
                : `${Math.round(calTarget - netCalories)} ${tr.remaining}`}
            </span>
          </div>
        )}
      </div>

      {hasFoodData && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Apple className="w-4 h-4 text-green-500" />
            <h3 className="text-sm font-bold text-stone-700 dark:text-slate-200">{tr.macros}</h3>
            <span className="ms-auto text-[10px] text-stone-400 dark:text-slate-500">
              {data.food.mealCount} {tr.meals}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-3 rounded-xl bg-orange-50 dark:bg-orange-500/10">
              <Beef className="w-4 h-4 text-orange-500 mx-auto mb-1" />
              <p className="text-base font-black text-orange-600 dark:text-orange-400">{Math.round(data.food.protein.value)}g</p>
              <p className="text-[10px] text-stone-500 dark:text-slate-400">{tr.proteinG}</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-blue-50 dark:bg-blue-500/10">
              <Apple className="w-4 h-4 text-blue-500 mx-auto mb-1" />
              <p className="text-base font-black text-blue-600 dark:text-blue-400">{Math.round(data.food.carbs.value)}g</p>
              <p className="text-[10px] text-stone-500 dark:text-slate-400">{tr.carbsG}</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-yellow-50 dark:bg-yellow-500/10">
              <Flame className="w-4 h-4 text-yellow-500 mx-auto mb-1" />
              <p className="text-base font-black text-yellow-600 dark:text-yellow-400">{Math.round(data.food.fat.value)}g</p>
              <p className="text-[10px] text-stone-500 dark:text-slate-400">{tr.fatG}</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-pink-50 dark:bg-pink-500/10">
              <Candy className="w-4 h-4 text-pink-500 mx-auto mb-1" />
              <p className="text-base font-black text-pink-600 dark:text-pink-400">
                {isUnknown(data.food.sugar) ? dash : `${Math.round(data.food.sugar.value)}g`}
              </p>
              <p className="text-[10px] text-stone-500 dark:text-slate-400">{tr.sugarG}</p>
            </div>
          </div>
          {data.food.fiber.value > 0 && (
            <div className="flex items-center justify-center gap-2 text-xs text-green-600 dark:text-green-400 pt-1">
              <Apple className="w-3.5 h-3.5" />
              <span>{tr.fiberG}: {Math.round(data.food.fiber.value)}g</span>
            </div>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-bold text-stone-700 dark:text-slate-200">{tr.adviceTitle}</h3>
        </div>
        <div className="space-y-2">
          {advice.map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-stone-600 dark:text-slate-300 leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl p-4 space-y-3 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-500/10 dark:to-blue-500/10 border border-indigo-100 dark:border-indigo-500/20">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-bold text-indigo-700 dark:text-indigo-300">{tr.tomorrowGoals}</h3>
        </div>
        <div className="space-y-2">
          {tomorrowGoals.map((g, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-indigo-600 dark:text-indigo-300 leading-relaxed">
              <Award className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
              <span>{g}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
